import { useState } from "react";
import {
  convertSecondsToFirestoreTimestamp,
  queryCollection,
} from "../models/DataFunctions";
import SmallSpinner from "../loading/SmallSpinner";
import SecondaryButton from "../buttons/SecondaryButton";
import PrimaryButton from "../buttons/PrimaryButton";
import DatePickerGPT0 from "../textfields/DatePickerGPT0";

const PAGE_SIZE = 20;

const PERIODS = [
  { label: "Cette semaine", value: "week" },
  { label: "Ce mois-ci", value: "month" },
  { label: "Cette année", value: "year" },
  { label: "Personnalisé", value: "custom" },
];

function getDateRange(period, customStart, customEnd) {
  const now = new Date();
  let start, end;

  end = new Date(now);
  end.setHours(23, 59, 59, 999);

  if (period === "week") {
    const day = now.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    start = new Date(now);
    start.setDate(now.getDate() + diffToMonday);
    start.setHours(0, 0, 0, 0);
  } else if (period === "month") {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    start.setHours(0, 0, 0, 0);
  } else if (period === "year") {
    start = new Date(now.getFullYear(), 0, 1);
    start.setHours(0, 0, 0, 0);
  } else {
    start = customStart ? new Date(customStart * 1000) : null;
    end = customEnd ? new Date(customEnd * 1000) : null;
    if (end) end.setHours(23, 59, 59, 999);
  }

  return { start, end };
}

export default function PartnerFolderExtractionModal({
  partnerData,
  orgData,
  onClose,
  navigateWorkspace,
}) {
  const [period, setPeriod] = useState("month");
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(0);

  const canSearch =
    period !== "custom" || (customStart != null && customEnd != null);

  const handleSearch = async () => {
    const { start, end } = getDateRange(period, customStart, customEnd);
    if (!start || !end) return;

    setLoading(true);
    setPage(0);
    setSearched(false);

    const filters = [
      { field: "master_id", operator: "==", value: orgData.id },
      {
        field: "created_time",
        operator: ">=",
        value: convertSecondsToFirestoreTimestamp(start.getTime() / 1000),
      },
      {
        field: "created_time",
        operator: "<=",
        value: convertSecondsToFirestoreTimestamp(end.getTime() / 1000),
      },
    ];

    const results = await queryCollection(
      "folder",
      filters,
      10000,
      "created_time",
      "desc"
    );

    const partnerFolders = results.filter(
      (f) =>
        (f.partners_id || []).includes(partnerData.id) ||
        f.client_id === partnerData.id
    );

    setFolders(partnerFolders);
    setSearched(true);
    setLoading(false);
  };

  const paginated = folders.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(folders.length / PAGE_SIZE);

  return (
    <div
      className="bg-white rounded-2xl flex flex-col overflow-hidden w-c600"
      style={{ maxHeight: "80vh" }}
    >
      {/* Header */}
      <div className="p-6 pb-4 border-b-1 flex-shrink-0">
        <div className="text-h2 font-semibold">
          Extraire les données — {partnerData.name}
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex flex-col gap-4 p-6 overflow-auto flex-1">
        {/* Period selector */}
        <div className="flex flex-row gap-2 flex-wrap">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => {
                setPeriod(p.value);
                setSearched(false);
              }}
              className={`px-3 py-1.5 rounded-lg text-h3 border transition-colors ${
                period === p.value
                  ? "bg-black text-white border-black"
                  : "bg-white text-black border-neutral-200 hover:bg-neutral-50"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Custom date range */}
        {period === "custom" && (
          <div className="flex flex-row gap-3 items-center">
            <DatePickerGPT0
              label="Date de début"
              seconds={customStart}
              onChange={(s) => {
                setCustomStart(s);
                setSearched(false);
              }}
              disableAfterToday={true}
              transparent={false}
            />
            <div className="text-shadow_50 text-h3 flex-shrink-0">→</div>
            <DatePickerGPT0
              label="Date de fin"
              seconds={customEnd}
              onChange={(s) => {
                setCustomEnd(s);
                setSearched(false);
              }}
              disableAfterToday={true}
              minDateSeconds={customStart}
              transparent={false}
            />
          </div>
        )}

        {/* Search button */}
        <div>
          <PrimaryButton
            onClick={handleSearch}
            disabled={loading || !canSearch}
            className="w-fit"
          >
            <div className="flex flex-row items-center gap-2">
              Rechercher
              <SmallSpinner loading={loading} size={18} margin={0} />
            </div>
          </PrimaryButton>
        </div>

        {/* Results */}
        {searched && !loading && (
          <div className="flex flex-col gap-3">
            <div className="text-h3 text-shadow_50">
              {folders.length} dossier{folders.length !== 1 ? "s" : ""} trouvé
              {folders.length !== 1 ? "s" : ""}
            </div>

            {folders.length > 0 && (
              <>
                <div className="flex flex-col border-1 rounded-xl overflow-hidden">
                  {paginated.map((folder, i) => (
                    <div
                      key={folder.id}
                      onClick={() => navigateWorkspace("/folder/" + folder.id)}
                      className={`flex flex-row items-center gap-3 px-4 py-3 cursor-pointer hover:bg-neutral-50 ${
                        i < paginated.length - 1 ? "border-b-1" : ""
                      }`}
                    >
                      <div className="flex flex-col flex-1 min-w-0">
                        <div className="text-h3 font-medium truncate">
                          {folder.prenom
                            ? `${folder.prenom} ${folder.nom}`
                            : folder.nom}
                        </div>
                        <div className="text-h3 text-shadow_50 truncate">
                          {[folder.adresse, folder.ville, folder.postal]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      </div>
                      <div className="text-h3 text-shadow_50 flex-shrink-0">
                        {folder.created_time?.seconds
                          ? new Date(
                              folder.created_time.seconds * 1000
                            ).toLocaleDateString("fr-FR", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })
                          : ""}
                      </div>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex flex-row items-center justify-between">
                    <SecondaryButton
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      Précédent
                    </SecondaryButton>
                    <div className="text-h3 text-shadow_50">
                      {page * PAGE_SIZE + 1}–
                      {Math.min((page + 1) * PAGE_SIZE, folders.length)} /{" "}
                      {folders.length}
                    </div>
                    <SecondaryButton
                      disabled={(page + 1) * PAGE_SIZE >= folders.length}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Suivant
                    </SecondaryButton>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-6 pt-4 border-t-1 flex-shrink-0">
        <SecondaryButton onClick={onClose}>Fermer</SecondaryButton>
      </div>
    </div>
  );
}
